import { useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCookieConsent, CookiePreferences } from "@/hooks/useCookieConsent";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const translations = {
  en: {
    title: "We value your privacy",
    description: "We use cookies to enhance your browsing experience and analyze our traffic. By clicking \"Accept All\", you consent to our use of cookies.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    customize: "Customize",
    learnMore: "Learn more in our",
    privacyPolicy: "Privacy Policy",
    preferencesTitle: "Cookie Preferences",
    preferencesDescription: "Manage your cookie preferences. Essential cookies cannot be disabled as they are required for the website to function.",
    essential: "Essential",
    essentialDesc: "Required for the website to function properly (authentication, security).",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors interact with our website.",
    marketing: "Marketing",
    marketingDesc: "Used to deliver personalized advertisements.",
    functional: "Functional",
    functionalDesc: "Enable enhanced functionality and personalization.",
    savePreferences: "Save Preferences",
    cancel: "Cancel",
  },
  fr: {
    title: "Nous respectons votre vie privée",
    description: "Nous utilisons des cookies pour améliorer votre expérience de navigation et analyser notre trafic. En cliquant sur \"Tout accepter\", vous consentez à notre utilisation des cookies.",
    acceptAll: "Tout accepter",
    rejectAll: "Tout refuser",
    customize: "Personnaliser",
    learnMore: "En savoir plus dans notre",
    privacyPolicy: "Politique de confidentialité",
    preferencesTitle: "Préférences des cookies",
    preferencesDescription: "Gérez vos préférences de cookies. Les cookies essentiels ne peuvent pas être désactivés car ils sont nécessaires au fonctionnement du site.",
    essential: "Essentiels",
    essentialDesc: "Nécessaires au bon fonctionnement du site (authentification, sécurité).",
    analytics: "Analytiques",
    analyticsDesc: "Nous aident à comprendre comment les visiteurs interagissent avec notre site.",
    marketing: "Marketing",
    marketingDesc: "Utilisés pour diffuser des publicités personnalisées.",
    functional: "Fonctionnels",
    functionalDesc: "Permettent des fonctionnalités améliorées et la personnalisation.",
    savePreferences: "Enregistrer",
    cancel: "Annuler",
  },
};

export function CookieBanner() {
  const { language } = useLanguage();
  const t = translations[language];
  const { showBanner, acceptAll, rejectAll, savePreferences, preferences } = useCookieConsent();
  const [showPreferences, setShowPreferences] = useState(false);
  const [tempPreferences, setTempPreferences] = useState<CookiePreferences>(preferences);

  const handleOpenPreferences = () => {
    setTempPreferences(preferences);
    setShowPreferences(true);
  };

  const handleSavePreferences = () => {
    savePreferences(tempPreferences);
    setShowPreferences(false);
  };

  if (!showBanner) return null;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
            <div className="mx-auto max-w-4xl rounded-xl border bg-card shadow-lg">
              <div className="p-4 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/20">
                    <Cookie className="h-5 w-5 text-accent" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-semibold text-foreground">{t.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t.description}{" "}
                      <Link to="/privacy" className="text-accent hover:underline">
                        {t.learnMore} {t.privacyPolicy}
                      </Link>
                    </p>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button onClick={acceptAll} className="bg-primary hover:bg-primary-hover">
                        {t.acceptAll}
                      </Button>
                      <Button variant="outline" onClick={rejectAll}>
                        {t.rejectAll}
                      </Button>
                      <Button variant="ghost" onClick={handleOpenPreferences}>
                        <Settings className="mr-2 h-4 w-4" />
                        {t.customize}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.preferencesTitle}</DialogTitle>
            <DialogDescription>{t.preferencesDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Essential - Always enabled */}
            <div className="flex items-start space-x-3 rounded-lg border p-3 bg-muted/30">
              <Checkbox id="essential" checked disabled className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="essential" className="font-medium">
                  {t.essential}
                </Label>
                <p className="text-xs text-muted-foreground">{t.essentialDesc}</p>
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start space-x-3 rounded-lg border p-3">
              <Checkbox
                id="analytics"
                checked={tempPreferences.analytics}
                onCheckedChange={(checked) =>
                  setTempPreferences((prev) => ({ ...prev, analytics: !!checked }))
                }
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="analytics" className="font-medium cursor-pointer">
                  {t.analytics}
                </Label>
                <p className="text-xs text-muted-foreground">{t.analyticsDesc}</p>
              </div>
            </div>

            {/* Marketing */}
            <div className="flex items-start space-x-3 rounded-lg border p-3">
              <Checkbox
                id="marketing"
                checked={tempPreferences.marketing}
                onCheckedChange={(checked) =>
                  setTempPreferences((prev) => ({ ...prev, marketing: !!checked }))
                }
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="marketing" className="font-medium cursor-pointer">
                  {t.marketing}
                </Label>
                <p className="text-xs text-muted-foreground">{t.marketingDesc}</p>
              </div>
            </div>

            {/* Functional */}
            <div className="flex items-start space-x-3 rounded-lg border p-3">
              <Checkbox
                id="functional"
                checked={tempPreferences.functional}
                onCheckedChange={(checked) =>
                  setTempPreferences((prev) => ({ ...prev, functional: !!checked }))
                }
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="functional" className="font-medium cursor-pointer">
                  {t.functional}
                </Label>
                <p className="text-xs text-muted-foreground">{t.functionalDesc}</p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowPreferences(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleSavePreferences} className="bg-primary hover:bg-primary-hover">
              {t.savePreferences}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
